import React, { useState, useRef } from 'react';
import { View, StyleSheet, PanResponder, TouchableOpacity } from 'react-native';
import { Surface, Text, useTheme } from 'react-native-paper';
import { TabContent } from './TabContent';
import { Tab, TabId, DEFAULT_TABS } from './types';
import { TreeNode } from '../TreeView';

interface MiddlePaneProps {
    selectedNode: TreeNode | null;
    projectData: any;
}

const TAB_WIDTH = 120;
const TAB_HEIGHT = 32;

export const MiddlePane: React.FC<MiddlePaneProps> = ({ selectedNode, projectData }) => {
    const theme = useTheme();
    const [tabs, setTabs] = useState<Tab[]>(DEFAULT_TABS);
    const [activeTabId, setActiveTabId] = useState<TabId>(DEFAULT_TABS[0].id);
    const [draggingTabId, setDraggingTabId] = useState<TabId | null>(null);
    const dragStartX = useRef(0);
    const dragStartOrder = useRef(0);

    // Get only visible tabs, sorted by order
    const visibleTabs = tabs.filter(tab => tab.isVisible).sort((a, b) => a.order - b.order);

    const findTabByPosition = (x: number) => {
        const index = Math.floor(x / TAB_WIDTH);
        return index >= 0 && index < visibleTabs.length ? visibleTabs[index] : null;
    };

    const reorderTabs = (draggedTab: Tab, targetOrder: number) => {
        if (draggedTab.order === targetOrder) return;

        setTabs(currentTabs => {
            return currentTabs.map(tab => {
                if (tab.id === draggedTab.id) {
                    return { ...tab, order: targetOrder };
                }
                if (draggedTab.order < targetOrder) {
                    if (tab.order <= targetOrder && tab.order > draggedTab.order) {
                        return { ...tab, order: tab.order - 1 };
                    }
                } else {
                    if (tab.order >= targetOrder && tab.order < draggedTab.order) {
                        return { ...tab, order: tab.order + 1 };
                    }
                }
                return tab;
            });
        });
    };

    const panResponder = PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: (_, gestureState) => {
            return Math.abs(gestureState.dx) > 5;
        },
        onPanResponderGrant: (event) => {
            const { locationX } = event.nativeEvent;
            const tab = findTabByPosition(locationX);
            if (tab) {
                setDraggingTabId(tab.id);
                dragStartX.current = locationX;
                dragStartOrder.current = tab.order;
            }
        },
        onPanResponderMove: (event) => {
            if (!draggingTabId) return;

            const { locationX } = event.nativeEvent;
            const draggedTab = tabs.find(t => t.id === draggingTabId);
            if (!draggedTab) return;

            const targetTab = findTabByPosition(locationX);
            if (targetTab && targetTab.id !== draggingTabId) {
                reorderTabs(draggedTab, targetTab.order);
            }
        },
        onPanResponderRelease: () => {
            setDraggingTabId(null);
            dragStartX.current = 0;
            dragStartOrder.current = 0;
        }
    });

    return (
        <Surface style={[styles.container, { backgroundColor: theme.colors.elevation.level1 }]}>
            <View style={styles.header}>
                <View 
                    style={[
                        styles.tabBar,
                        { borderBottomColor: theme.colors.outline }
                    ]} 
                    {...panResponder.panHandlers}
                >
                    {visibleTabs.map((tab, index) => (
                        <TouchableOpacity
                            key={tab.id}
                            onPress={() => setActiveTabId(tab.id)}
                            style={[
                                styles.tab,
                                {
                                    backgroundColor: activeTabId === tab.id 
                                        ? theme.colors.primary
                                        : theme.colors.elevation.level2,
                                    borderColor: activeTabId === tab.id
                                        ? theme.colors.primary
                                        : theme.colors.outline,
                                    zIndex: activeTabId === tab.id ? 1 : 0,
                                    marginLeft: index > 0 ? -1 : 0, // Overlap borders
                                },
                                activeTabId === tab.id && styles.activeTab,
                                draggingTabId === tab.id && styles.draggingTab
                            ]}
                        >
                            <Text 
                                style={[
                                    styles.tabText,
                                    { 
                                        color: activeTabId === tab.id
                                            ? theme.colors.onPrimary
                                            : theme.colors.onSurface,
                                        opacity: activeTabId === tab.id ? 1 : 0.7,
                                        fontWeight: activeTabId === tab.id ? '600' : '400'
                                    }
                                ]}
                            >
                                {tab.title}
                            </Text>
                        </TouchableOpacity>
                    ))}
                </View>
            </View>

            <View style={styles.content}>
                <TabContent tabId={activeTabId} selectedNode={selectedNode} projectData={projectData} />
            </View>
        </Surface>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%'
    },
    header: {
        width: '100%',
    },
    tabBar: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        paddingLeft: 16,
        paddingTop: 8,
        height: TAB_HEIGHT + 8, // Add padding to height
    },
    tab: {
        height: TAB_HEIGHT,
        width: TAB_WIDTH,
        borderWidth: 1,
        borderBottomWidth: 0,
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        justifyContent: 'center',
        alignItems: 'center',
    },
    activeTab: {
        borderBottomWidth: 0,
        marginBottom: -1, // Cover the tab bar border
    },
    draggingTab: {
        opacity: 0.8,
        transform: [{ scale: 1.02 }],
    },
    tabText: {
        fontSize: 13,
        fontWeight: '500',
    },
    content: {
        flex: 1,
        width: '100%',
        height: '100%'
    }
});
